import React from 'react';
import styled from 'styled-components';
import { Text, Button } from '@companycam/slab-web';
import { useIntl, defineMessages } from 'react-intl';

import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import TrustyHypeGraphic from 'images/TrustyHypeGraphic@2x.png';
import TrustyAppLockup from 'images/TrustyAppLockup.svg';
import Common from 'helpers/intl-common';

const messages = defineMessages({
  title: {
    id: 'Customers.hypeModal.title',
    defaultMessage: 'An easier way to coordinate with your customers.',
    description: 'Hype modal title',
  },
  description: {
    id: 'Customers.hypeModal.description',
    defaultMessage:
      'Invite customers to use Trusty, the new customer portal from CompanyCam, where they can:',
    description: 'Hype modal description',
  },
  valueProp1: {
    id: 'Customers.hypeModal.checklist.one',
    defaultMessage: 'Take and upload photos that sync with CompanyCam',
    description: 'First checklist item',
  },
  valueProp2: {
    id: 'Customers.hypeModal.checklist.two',
    defaultMessage: 'Pay with credit card, ACH, Apple Pay or Google Pay',
    description: 'Second checklist item',
  },
  valueProp3: {
    id: 'Customers.hypeModal.checklist.three',
    defaultMessage: 'Stay up-to-date with project notifications',
    description: 'Third checklist item',
  },
  logoAltText: {
    id: 'Customers.hypeModal.logoAltText',
    defaultMessage: 'Trusty logo',
    description: 'Logo alt text',
  },
  imageAltText: {
    id: 'Customers.hypeModal.imageAltText',
    defaultMessage: 'Trusty app on a phone',
    description: 'Image alt text',
  },
});

type TrustyHypeModalProps = {
  onRequestClose: () => void;
  onContinueClicked: () => void;
  onLearnMoreClicked: () => void;
};

// #region styled-components
const HypeModal = styled(CCModal)`
  width: 980px;
`;
const ContentContainer = styled.div`
  display: flex;
  overflow: hidden;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  border-radius: var(--cc_size_border_radius_xl);
  background-color: var(--cc_color_background_1);
`;
const ValuePropContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 56px;
`;
const ValuePropList = styled.ul`
  margin-left: 20px;
  padding: 0;
  list-style-type: circle;
  display: flex;
  flex-direction: column;
  gap: calc(var(--cc_size_spacing_s) + var(--cc_size_spacing_xs));
`;
const ButtonContainer = styled.div`
  display: flex;
  margin-top: var(--cc_size_spacing_xl);
  gap: var(--cc_size_spacing_m);
`;
const TrustyGraphic = styled.img`
  display: flex;
  width: 460px;
  padding: 0;
  @media screen and (max-width: 600px) {
    display: none;
  }
`;
const TrustyLogo = styled.img`
  display: flex;
  width: 175px;
  padding: 0;
`;
// #endregion

const PaymentValueProps = () => {
  const { formatMessage } = useIntl();

  const valuePropMessages = [
    messages.valueProp1,
    messages.valueProp2,
    messages.valueProp3,
  ];

  return (
    <ValuePropList>
      {valuePropMessages.map((message) => (
        <li key={message.id}>
          <Text
            ccMargin="none none none s"
            size="s"
            family="heading"
            weight="500"
            as="p"
          >
            {formatMessage(message)}
          </Text>
        </li>
      ))}
    </ValuePropList>
  );
};

const TrustyHypeModal = ({
  onRequestClose,
  onContinueClicked,
  onLearnMoreClicked,
}: TrustyHypeModalProps) => {
  const { formatMessage } = useIntl();

  return (
    <HypeModal
      size="medium"
      bodyStyle={{ padding: 0 }}
      isOpen
      onRequestClose={onRequestClose}
    >
      <ContentContainer data-testid="project__customers__trusty-hype-modal">
        <ValuePropContainer>
          <TrustyLogo
            src={TrustyAppLockup}
            alt={formatMessage(messages.logoAltText)}
          />
          <Text
            as="h1"
            size="xxl"
            ccMargin="l none none"
            family="heading"
            weight="700"
          >
            {formatMessage(messages.title)}
          </Text>
          <Text
            size="m"
            ccMargin="m none m"
            family="heading"
            weight="500"
            as="h2"
          >
            {formatMessage(messages.description)}
          </Text>
          <PaymentValueProps />
          <ButtonContainer>
            <Button
              color="secondary"
              href=""
              onClick={onContinueClicked}
              data-testid="project__customers__trusty-hype-modal-continue"
            >
              {formatMessage(Common.continue)}
            </Button>
            <Button
              color="subtle"
              href="https://help.companycam.com/en/articles/8011030-how-to-give-project-access-using-trusty-beta"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onLearnMoreClicked}
              data-testid="project__customers__trusty-hype-modal-learn-more"
            >
              {formatMessage(Common.learnMore)}
            </Button>
          </ButtonContainer>
        </ValuePropContainer>
        <TrustyGraphic
          src={TrustyHypeGraphic}
          alt={formatMessage(messages.imageAltText)}
        />
      </ContentContainer>
    </HypeModal>
  );
};

export default TrustyHypeModal;
